import { useEffect, useMemo, useRef, useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import type { LayerSpecification } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../urbanmap/_components/UMMap';
import { UMR, UM_MAPBOX_STYLES } from '../../urbanmap/_components/referenceTokens';
import { boundsOf } from './domain';
import { buildingsFC, ringFC } from './mapLayers';
import { PC } from './tokens';
import type { LngLat, ProjectDetail, Referential } from './types';

const layers: LayerSpecification[] = [
  { id: 'mini-buildings', type: 'fill', source: 'buildings', paint: { 'fill-color': PC.included, 'fill-opacity': 0.65 } },
  { id: 'mini-area', type: 'fill', source: 'outline', paint: { 'fill-color': PC.outline, 'fill-opacity': 0.08 } },
  { id: 'mini-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2 } },
];

export function ProjectMiniMap({ detail, referential }: { detail: ProjectDetail; referential: Referential }) {
  const ref = useRef<HTMLDivElement>(null);
  const token = getMapboxToken();
  const [error, setError] = useState<string>();
  const rings = useMemo(() => {
    const perimeter = detail.project.perimeter;
    return perimeter.kind === 'drawn' ? [perimeter.polygon]
      : perimeter.communes.map((id) => referential.communeOutlines[id]).filter((ring): ring is LngLat[] => !!ring);
  }, [detail.project.perimeter, referential.communeOutlines]);
  const bounds = useMemo(() => boundsOf(rings.length ? rings.flat() : detail.buildings.map((building) => building.centroid)), [rings, detail.buildings]);
  const sources = useMemo(() => ({
    buildings: buildingsFC(detail.buildings, () => ({})),
    outline: ringFC(rings),
  }), [detail.buildings, rings]);
  const offlinePaths = useMemo(() => {
    const sw = mapboxgl.MercatorCoordinate.fromLngLat(bounds[0]);
    const ne = mapboxgl.MercatorCoordinate.fromLngLat(bounds[1]);
    const span = Math.max(ne.x - sw.x, sw.y - ne.y);
    const scale = 260 / span;
    const point = (p: number[]) => {
      const m = mapboxgl.MercatorCoordinate.fromLngLat([p[0], p[1]]);
      return `${150 + (m.x - (ne.x + sw.x) / 2) * scale},${150 + (m.y - (ne.y + sw.y) / 2) * scale}`;
    };
    const path = (polygon: number[][][]) => polygon.map((ring) => `M${ring.map(point).join('L')}Z`).join('');
    return {
      buildings: detail.buildings.map((building) => building.geometry.type === 'Polygon'
        ? path(building.geometry.coordinates) : building.geometry.coordinates.map(path).join('')),
      outlines: rings.map((ring) => path([ring])),
    };
  }, [bounds, detail.buildings, rings]);

  useEffect(() => {
    if (!ref.current || !token) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      accessToken: token || undefined,
      style: UM_MAPBOX_STYLES.find((style) => style.id === 'light')!.style,
      bounds,
      fitBoundsOptions: { padding: 20, maxZoom: 17 },
      interactive: false,
      // These maps are static thumbnails and must retain their pixels in captures.
      preserveDrawingBuffer: true,
      attributionControl: true,
      logoPosition: 'bottom-right',
    });
    const fit = () => { map.resize(); map.fitBounds(bounds, { padding: 20, maxZoom: 17, duration: 0 }); };
    fit();
    map.getCanvas().setAttribute('tabindex', '-1');
    map.on('load', () => {
      Object.entries(sources).forEach(([id, data]) => map.addSource(id, { type: 'geojson', data }));
      layers.forEach((layer) => map.addLayer(layer));
    });
    map.on('error', (event) => setError(event.error.message));
    const resize = new ResizeObserver(fit);
    resize.observe(map.getContainer());
    return () => {
      resize.disconnect();
      map.remove();
    };
  }, [bounds, sources, token]);

  return (
    <div className="pcv3-wf-map" aria-label={`Carte du périmètre de ${detail.project.name}`}>
      <div ref={ref} className="pcv3-map-surface" />
      {!token && (
        <svg className="pcv3-map-surface" viewBox="0 0 300 300" role="img" aria-label={`Bâtiments et contour du périmètre de ${detail.project.name}`}>
          <rect width="300" height="300" fill={UMR.map.fallbackLand} />
          {offlinePaths.buildings.map((d, index) => <path key={index} d={d} fill={PC.included} fillOpacity={0.65} fillRule="evenodd" />)}
          {offlinePaths.outlines.map((d, index) => <path key={index} d={d} fill={PC.outline} fillOpacity={0.08} stroke={PC.outline} strokeWidth={2} fillRule="evenodd" />)}
        </svg>
      )}
      {error ? <NJText role="alert" scale="sm" className="pcv3-map-mode">Erreur de carte : {error}</NJText>
        : !token && <NJText scale="sm" className="pcv3-map-mode">Aperçu géographique hors ligne · sans fond Mapbox</NJText>}
    </div>
  );
}
